(() => {
  const SELECTED_UPSELL_EVENT = 'sleepcity:bundle-change';
  const STRICT_MATCH_DEFAULT = 'upsell_match_variant';

  function parseJsonScript(root, selector) {
    const script = root.querySelector(selector);

    if (!script) return {};

    try {
      return JSON.parse(script.textContent || '{}');
    } catch (error) {
      console.error('SleepCity PDP configuration could not be parsed.', error);
      return {};
    }
  }

  function formatMoney(cents, moneyFormat) {
    const value = Number(cents) || 0;

    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(value, moneyFormat);
    }

    const format = moneyFormat || '${{amount}}';
    const placeholder = format.match(/\{\{\s*(\w+)\s*\}\}/);

    if (!placeholder) return (value / 100).toFixed(2);

    const amount = value / 100;
    let formattedAmount;

    switch (placeholder[1]) {
      case 'amount_no_decimals':
        formattedAmount = Math.round(amount).toLocaleString('en-US');
        break;
      case 'amount_with_comma_separator':
        formattedAmount = amount.toLocaleString('de-DE', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
        break;
      case 'amount_no_decimals_with_comma_separator':
        formattedAmount = Math.round(amount).toLocaleString('de-DE');
        break;
      default:
        formattedAmount = amount.toLocaleString('en-US', {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
    }

    return format.replace(placeholder[0], formattedAmount);
  }

  function getAvailableVariant(product) {
    if (!Array.isArray(product.variants)) return null;
    return product.variants.find((variant) => variant.available) || null;
  }

  class SleepCityUpsells extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.abortController = new AbortController();
      this.config = parseJsonScript(this, '[data-upsell-config]');
      this.moneyFormat = this.config.moneyFormat;
      this.mainVariants = Array.isArray(this.config.mainVariants) ? this.config.mainVariants : [];
      this.strictMatchTag = String(this.config.strictMatchTag || STRICT_MATCH_DEFAULT).toLowerCase();
      this.productInfo = this.closest('product-info');
      this.status = this.querySelector('[data-upsell-status]');
      this.categories = Array.from(this.querySelectorAll('[data-upsell-category]'));

      this.bindEvents();
      this.loadRecommendations();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.initialized = false;
    }

    bindEvents() {
      if (!this.productInfo) return;

      const signal = this.abortController.signal;

      this.productInfo.addEventListener(
        'change',
        (event) => {
          if (
            event.target.closest('variant-selects') ||
            event.target.closest('variant-radios') ||
            event.target.matches('[name="id"]')
          ) {
            requestAnimationFrame(() => this.syncStrictVariants());
          }
        },
        { signal }
      );
    }

    async loadRecommendations() {
      const url = this.dataset.recommendationsUrl;

      if (!url || this.categories.length === 0) {
        this.setStatus(this.dataset.emptyLabel);
        return;
      }

      try {
        const response = await fetch(url, {
          headers: { Accept: 'application/json' },
          signal: this.abortController.signal,
        });

        if (!response.ok) throw new Error(`Recommendations request failed with status ${response.status}.`);

        const data = await response.json();
        const products = Array.isArray(data.products) ? data.products : [];
        let renderedCount = 0;

        products.forEach((product) => {
          const category = this.findCategory(product);
          if (!category) return;

          const card = this.createCard(product);
          if (!card) return;

          category.querySelector('[data-upsell-grid]')?.appendChild(card);
          category.hidden = false;
          renderedCount += 1;
        });

        if (renderedCount === 0) {
          this.setStatus(this.dataset.emptyLabel);
          return;
        }

        this.setStatus('');
        this.syncStrictVariants();
      } catch (error) {
        if (error.name === 'AbortError') return;

        console.error('SleepCity upsell recommendations failed.', error);
        this.setStatus(this.dataset.errorLabel);
      }
    }

    findCategory(product) {
      const productTags = new Set(
        (product.tags || []).map((tag) => String(tag).trim().toLowerCase())
      );

      return this.categories.find((category) => {
        const triggerTags = String(category.dataset.triggerTags || '')
          .split(',')
          .map((tag) => tag.trim().toLowerCase())
          .filter(Boolean);

        return triggerTags.some((tag) => productTags.has(tag));
      });
    }

    createCard(product) {
      const initialVariant = getAvailableVariant(product);
      if (!initialVariant) return null;

      const card = document.createElement('article');
      const strictMatch = (product.tags || []).some(
        (tag) => String(tag).trim().toLowerCase() === this.strictMatchTag
      );

      card.className = 'sleep-city-custom-pdp__upsell-card';
      card.dataset.productId = String(product.id);
      card.dataset.strictMatch = String(strictMatch);
      card.productData = product;

      const mediaLink = document.createElement('a');
      mediaLink.className = 'sleep-city-custom-pdp__upsell-media';
      mediaLink.href = product.url;

      if (product.featured_image) {
        const image = document.createElement('img');
        image.src = product.featured_image;
        image.alt = product.title || '';
        image.loading = 'lazy';
        mediaLink.appendChild(image);
      }

      const content = document.createElement('div');
      content.className = 'sleep-city-custom-pdp__upsell-content';

      const title = document.createElement('a');
      title.className = 'sleep-city-custom-pdp__upsell-title';
      title.href = product.url;
      title.textContent = product.title;

      const price = document.createElement('span');
      price.className = 'sleep-city-custom-pdp__upsell-price';
      price.dataset.upsellPrice = '';
      price.textContent = `+${formatMoney(initialVariant.price, this.moneyFormat)}`;

      content.append(title, price);

      if (!strictMatch) {
        const select = this.createVariantSelect(product, initialVariant);
        if (select) content.appendChild(select);
      }

      const toggle = document.createElement('button');
      toggle.type = 'button';
      toggle.className = 'sleep-city-custom-pdp__upsell-toggle';
      toggle.dataset.upsellToggle = '';
      toggle.dataset.variantId = String(initialVariant.id);
      toggle.dataset.price = String(initialVariant.price);
      toggle.setAttribute('aria-pressed', 'false');
      toggle.textContent = 'Add';
      toggle.addEventListener('click', () => this.toggleSelection(toggle));

      card.append(mediaLink, content, toggle);
      return card;
    }

    createVariantSelect(product, selectedVariant) {
      const availableVariants = product.variants.filter((variant) => variant.available);
      if (availableVariants.length <= 1) return null;

      const label = document.createElement('label');
      const select = document.createElement('select');
      const selectId = `UpsellVariant-${this.dataset.sectionId}-${product.id}`;

      label.className = 'visually-hidden';
      label.htmlFor = selectId;
      label.textContent = `Choose an option for ${product.title}`;

      select.id = selectId;
      select.className = 'sleep-city-custom-pdp__upsell-select';
      select.setAttribute('aria-label', label.textContent);

      availableVariants.forEach((variant) => {
        const option = document.createElement('option');
        option.value = String(variant.id);
        option.dataset.price = String(variant.price);
        option.textContent = variant.public_title || variant.title || 'Default';
        option.selected = String(variant.id) === String(selectedVariant.id);
        select.appendChild(option);
      });

      select.addEventListener('change', () => {
        const card = select.closest('.sleep-city-custom-pdp__upsell-card');
        const selectedOption = select.selectedOptions[0];
        const button = card?.querySelector('[data-upsell-toggle]');
        const price = card?.querySelector('[data-upsell-price]');

        if (!button || !selectedOption) return;

        button.dataset.variantId = selectedOption.value;
        button.dataset.price = selectedOption.dataset.price || '0';

        if (price) {
          price.textContent = `+${formatMoney(button.dataset.price, this.moneyFormat)}`;
        }

        if (button.getAttribute('aria-pressed') === 'true') this.notifyBundle();
      });

      const wrapper = document.createElement('div');
      wrapper.className = 'sleep-city-custom-pdp__upsell-select-wrapper';
      wrapper.append(label, select);
      return wrapper;
    }

    toggleSelection(button) {
      if (button.disabled) return;

      const selected = button.getAttribute('aria-pressed') !== 'true';
      button.setAttribute('aria-pressed', String(selected));
      button.textContent = selected ? 'Added' : 'Add';
      this.notifyBundle();
    }

    getMainVariant() {
      const form = document.getElementById(this.dataset.productFormId);
      const variantId = form?.querySelector('[name="id"]')?.value;
      return this.mainVariants.find((variant) => String(variant.id) === String(variantId)) || null;
    }

    syncStrictVariants() {
      const mainVariant = this.getMainVariant();
      const mainValue = mainVariant?.option1
        ? String(mainVariant.option1).trim().toLowerCase()
        : '';

      this.querySelectorAll('[data-strict-match="true"]').forEach((card) => {
        const product = card.productData;
        const button = card.querySelector('[data-upsell-toggle]');
        const price = card.querySelector('[data-upsell-price]');
        const matchedVariant = product?.variants?.find((variant) => {
          if (!variant.available || !mainValue) return false;

          return [variant.option1, variant.option2, variant.option3]
            .filter(Boolean)
            .some((option) => String(option).trim().toLowerCase() === mainValue);
        });

        if (!button) return;

        if (!matchedVariant) {
          card.classList.add('is-unavailable');
          button.disabled = true;
          button.setAttribute('aria-pressed', 'false');
          button.textContent = 'Unavailable';
          button.removeAttribute('data-variant-id');
          button.dataset.price = '0';
          return;
        }

        card.classList.remove('is-unavailable');
        button.disabled = false;
        button.dataset.variantId = String(matchedVariant.id);
        button.dataset.price = String(matchedVariant.price);
        button.textContent =
          button.getAttribute('aria-pressed') === 'true' ? 'Added' : 'Add';

        if (price) {
          price.textContent = `+${formatMoney(matchedVariant.price, this.moneyFormat)}`;
        }
      });

      this.notifyBundle();
    }

    notifyBundle() {
      this.productInfo?.dispatchEvent(
        new CustomEvent(SELECTED_UPSELL_EVENT, {
          bubbles: true,
          detail: { sectionId: this.dataset.sectionId },
        })
      );
    }

    setStatus(message) {
      if (!this.status) return;
      this.status.textContent = message || '';
      this.status.hidden = !message;
    }
  }

  class SleepCityBundle extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;

      this.initialized = true;
      this.abortController = new AbortController();
      this.config = parseJsonScript(this, '[data-bundle-config]');
      this.moneyFormat = this.config.moneyFormat;
      this.variants = new Map(
        (this.config.variants || []).map((variant) => [String(variant.id), variant])
      );
      this.productInfo = this.closest('product-info');
      this.form = document.getElementById(this.dataset.productFormId);
      this.submitButton = this.querySelector('[data-bundle-submit]');
      this.status = this.querySelector('[data-bundle-status]');
      this.variantPending = false;
      this.isSubmitting = false;

      this.bindEvents();
      this.recalculate();
    }

    disconnectedCallback() {
      this.abortController?.abort();
      this.unsubscribeVariantChange?.();
      this.initialized = false;
    }

    bindEvents() {
      if (!this.productInfo || !this.submitButton) return;

      const signal = this.abortController.signal;

      this.productInfo.addEventListener(
        SELECTED_UPSELL_EVENT,
        () => this.scheduleRecalculation(),
        { signal }
      );

      this.productInfo.addEventListener(
        'change',
        (event) => {
          const changedVariantOption =
            event.target.closest('variant-selects') ||
            event.target.closest('variant-radios');

          if (changedVariantOption && !event.target.matches('[name="id"]')) {
            this.setVariantPending(true);
            return;
          }

          if (event.target.matches('[name="id"]')) {
            this.setVariantPending(false);
            return;
          }

          if (event.target.matches('[name="quantity"]')) {
            this.scheduleRecalculation();
          }
        },
        { signal }
      );

      this.productInfo.addEventListener(
        'click',
        (event) => {
          if (event.target.closest('quantity-input button')) {
            this.scheduleRecalculation();
          }
        },
        { signal }
      );

      this.submitButton.addEventListener('click', () => this.addBundle(), { signal });

      if (
        typeof subscribe === 'function' &&
        typeof PUB_SUB_EVENTS !== 'undefined' &&
        PUB_SUB_EVENTS.variantChange
      ) {
        this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
          const changedSectionId = event?.data?.sectionId;

          if (
            changedSectionId != null &&
            String(changedSectionId) !== String(this.dataset.sectionId)
          ) {
            return;
          }

          this.setVariantPending(false);
        });
      }
    }

    scheduleRecalculation() {
      cancelAnimationFrame(this.recalculationFrame);
      this.recalculationFrame = requestAnimationFrame(() => this.recalculate());
    }

    getVariantInput() {
      return this.form?.querySelector('[name="id"]') || null;
    }

    getQuantity() {
      const input =
        this.form?.querySelector('[name="quantity"]') ||
        this.productInfo?.querySelector('[name="quantity"]');
      const quantity = Number.parseInt(input?.value || '1', 10);
      return Number.isNaN(quantity) || quantity < 1 ? 1 : quantity;
    }

    getSelectedUpsells() {
      if (!this.productInfo) return [];

      return Array.from(
        this.productInfo.querySelectorAll(
          'sleep-city-upsells [data-upsell-toggle][aria-pressed="true"]:not(:disabled)'
        )
      );
    }

    recalculate() {
      const variant = this.variants.get(String(this.getVariantInput()?.value || ''));
      const quantity = this.getQuantity();
      const selectedUpsells = this.getSelectedUpsells();
      const upsellTotal = selectedUpsells.reduce(
        (total, button) => total + (Number.parseInt(button.dataset.price || '0', 10) || 0),
        0
      );
      const basePrice = (variant?.price || 0) * quantity;
      const baseCompare = (variant?.compareAtPrice || variant?.price || 0) * quantity;
      const total = basePrice + upsellTotal;
      const compareTotal = baseCompare + upsellTotal;
      const savings = Math.max(compareTotal - total, 0);

      this.querySelector('[data-bundle-total]').textContent = formatMoney(total, this.moneyFormat);

      const compare = this.querySelector('[data-bundle-compare]');
      const savingsElement = this.querySelector('[data-bundle-savings]');

      if (compare && savingsElement) {
        compare.textContent = formatMoney(compareTotal, this.moneyFormat);
        savingsElement.textContent =
          `${this.dataset.saveLabel} ${formatMoney(savings, this.moneyFormat)}`.trim();
        compare.hidden = savings === 0;
        savingsElement.hidden = savings === 0;
      }

      this.syncButtonState(false, selectedUpsells.length);
    }

    getPurchaseLabel(selectedUpsellCount = this.getSelectedUpsells().length) {
      return selectedUpsellCount > 0
        ? this.submitButton.dataset.bundleLabel
        : this.submitButton.dataset.singleLabel;
    }

    buildMainItem(variantId) {
      const item = {
        id: String(variantId),
        quantity: this.getQuantity(),
      };

      if (!this.form) return item;

      const formData = new FormData(this.form);
      const sellingPlan = formData.get('selling_plan');
      const properties = {};

      formData.forEach((value, name) => {
        if (!name.startsWith('properties[') || typeof value !== 'string' || value === '') return;
        const propertyName = name.slice('properties['.length, -1);
        if (propertyName) properties[propertyName] = value;
      });

      if (sellingPlan) item.selling_plan = Number(sellingPlan);
      if (Object.keys(properties).length > 0) item.properties = properties;

      return item;
    }

    async addBundle() {
      const variantId = this.getVariantInput()?.value;
      const variant = this.variants.get(String(variantId || ''));

      if (!variantId || !variant?.available || this.submitButton.disabled) return;

      this.setLoading(true);
      this.setStatus('');

      const items = [this.buildMainItem(variantId)];

      this.getSelectedUpsells().forEach((button) => {
        const upsellVariantId = button.dataset.variantId;
        if (!upsellVariantId) return;
        items.push({ id: upsellVariantId, quantity: 1 });
      });

      const cart =
        document.querySelector('cart-notification') ||
        document.querySelector('cart-drawer');
      const body = { items };

      if (cart && typeof cart.getSectionsToRender === 'function') {
        body.sections = cart.getSectionsToRender().map((section) => section.id);
        body.sections_url = window.location.pathname;
      }

      const root = window.Shopify?.routes?.root || '/';
      const endpoint = window.routes?.cart_add_url || `${root}cart/add.js`;

      try {
        const response = await fetch(endpoint, {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(body),
        });
        const result = await response.json();

        if (!response.ok || result.status) {
          throw new Error(result.description || result.message || 'Bundle add failed.');
        }

        if (cart && typeof cart.renderContents === 'function' && result.sections) {
          cart.renderContents(result);
          this.setLoading(false);
          this.setStatus(this.submitButton.dataset.successLabel);
          return;
        }

        window.location.assign(`${root}cart`);
      } catch (error) {
        console.error('SleepCity bundle add failed.', error);
        this.setLoading(false, true);
        this.setStatus(this.dataset.errorMessage);
      }
    }

    setLoading(loading, hasError = false) {
      this.isSubmitting = loading;

      if (!loading) this.recalculate();
      this.syncButtonState(hasError);
    }

    setVariantPending(pending) {
      if (this.variantPending === pending) return;

      this.variantPending = pending;
      this.productInfo?.setAttribute('aria-busy', String(pending));
      this.setAttribute('aria-busy', String(pending));

      const price = this.productInfo?.querySelector(`#price-${this.dataset.sectionId}`);
      price?.setAttribute('aria-busy', String(pending));

      if (!pending) this.recalculate();
      this.syncButtonState();
    }

    syncButtonState(hasError = false, selectedUpsellCount = this.getSelectedUpsells().length) {
      const variant = this.variants.get(String(this.getVariantInput()?.value || ''));
      const busy = this.variantPending || this.isSubmitting;
      const label = this.submitButton.querySelector('[data-bundle-submit-label]');
      const spinner = this.submitButton.querySelector('.loading__spinner');

      this.submitButton.classList.toggle('loading', busy);
      this.submitButton.disabled = busy || !variant?.available;
      spinner?.classList.toggle('hidden', !busy);

      if (label) {
        label.textContent = this.variantPending
          ? this.submitButton.dataset.variantLoadingLabel
          : this.isSubmitting
          ? this.submitButton.dataset.loadingLabel
          : hasError
            ? this.submitButton.dataset.errorLabel
            : this.getPurchaseLabel(selectedUpsellCount);
      }
    }

    setStatus(message) {
      if (!this.status) return;
      this.status.textContent = message || '';
      this.status.hidden = !message;
    }
  }

  if (!customElements.get('sleep-city-upsells')) {
    customElements.define('sleep-city-upsells', SleepCityUpsells);
  }

  if (!customElements.get('sleep-city-bundle')) {
    customElements.define('sleep-city-bundle', SleepCityBundle);
  }

})();
